import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MatRippleModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltipModule } from '@angular/material/tooltip'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateModule } from '@ngx-translate/core'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { ColorPickerDirective } from 'ngx-color-picker'
import { GbColorPickerComponent } from '../gb-color-picker/gb-color-picker.component'
import { GbIconComponent } from '../gb-icon/gb-icon.component'
import { IconLibrary } from '../gb-icon/gb-icon-library.models'

type EyeDropperOpenResult = {
  sRGBHex: string
}

type EyeDropperInstance = {
  open: () => Promise<EyeDropperOpenResult>
}

type EyeDropperConstructor = {
  new (): EyeDropperInstance
}

@UntilDestroy()
@Component({
  selector: 'gb-token-color-picker',
  templateUrl: './gb-token-color-picker.component.html',
  styleUrls: ['./gb-token-color-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatRippleModule,
    MatTooltipModule,
    TranslateModule,
    ColorPickerDirective,
    GbIconComponent,
    GbColorPickerComponent,
  ],
})
export class GbTokenColorPickerComponent implements OnInit {
  @Input() labelTranslateKey = 'PRESENTER.CUSTOM_COLOR_HEX'
  @Input({ required: true }) control!: FormControl
  @Input() cpPosition: 'left' | 'right' = 'right'
  @Input() circleDiameter = '38px'
  @Input() showDropdown = true
  @Input() showEyeDropper = true

  @Output() colorChange = new EventEmitter<string>()
  @Output() colorPickerClose = new EventEmitter<string>()

  dropdownControl = new FormControl('custom_color')
  colorPickerControl = new FormControl('')
  colorPickerValue = ''

  eyeDropperSupported = false

  IconLibrary = IconLibrary

  readonly colorTokens = [
    'primary_color',
    'background_color',
    'accent_color',
    'banner_background_color',
    'warning_color',
    'success_color',
  ]

  constructor(
    private colorResolver: GbPresenterColorResolverService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.control) return

    this.eyeDropperSupported = this.getEyeDropperConstructor() !== undefined

    this.parseMainControlValue(this.control.value)
    this.colorPickerValue = this.colorPickerControl.value || ''

    this.colorPickerControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      this.colorPickerValue = val || ''
    })

    this.control.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      this.parseMainControlValue(val)
    })

    this.dropdownControl.valueChanges.pipe(untilDestroyed(this)).subscribe((selectedOption) => {
      if (selectedOption === 'custom_color') {
        const hex = this.colorPickerControl.value || ''
        if (this.control.value !== hex) {
          this.control.setValue(hex)
          this.colorChange.emit(hex)
          this.colorPickerClose.emit(hex)
        }
      } else if (selectedOption && this.colorTokens.includes(selectedOption)) {
        if (this.control.value !== selectedOption) {
          this.control.setValue(selectedOption)
          this.colorChange.emit(selectedOption)
          this.colorPickerClose.emit(selectedOption)
        }
      }
    })

    this.colorPickerControl.valueChanges.pipe(untilDestroyed(this)).subscribe((hexValue) => {
      if (this.dropdownControl.value === 'custom_color') {
        if (this.control.value !== hexValue) {
          this.control.setValue(hexValue)
          this.colorChange.emit(hexValue || '')
          if (!hexValue) {
            this.colorPickerClose.emit('')
          }
        }
      }
    })
  }

  private parseMainControlValue(val: string | null | undefined): void {
    const value = val || ''
    if (this.colorTokens.includes(value)) {
      this.dropdownControl.setValue(value, { emitEvent: false })
    } else {
      this.dropdownControl.setValue('custom_color', { emitEvent: false })
      this.colorPickerControl.setValue(value, { emitEvent: false })
    }
  }

  private getEyeDropperConstructor(): EyeDropperConstructor | undefined {
    const windowWithEyeDropper = window as unknown as { EyeDropper?: EyeDropperConstructor }
    return windowWithEyeDropper.EyeDropper
  }

  async pickColorFromScreen(): Promise<void> {
    const EyeDropper = this.getEyeDropperConstructor()
    if (!EyeDropper) return

    try {
      const instance = new EyeDropper()
      const result = await instance.open()

      this.dropdownControl.setValue('custom_color', { emitEvent: false })
      this.colorPickerValue = result.sRGBHex
      this.colorPickerControl.setValue(result.sRGBHex, { emitEvent: false })

      if (this.control.value !== result.sRGBHex) {
        this.control.setValue(result.sRGBHex)
        this.colorChange.emit(result.sRGBHex)
        this.colorPickerClose.emit(result.sRGBHex)
      }

      this.cdr.markForCheck()
    } catch {
      return
    }
  }

  getPreviewColor(
    dropdownValue: string | null | undefined,
    colorPickerControlValue: string | null | undefined,
  ): string {
    if (dropdownValue === 'custom_color') {
      return this.colorResolver.resolve(colorPickerControlValue)
    }
    return this.colorResolver.resolve(dropdownValue)
  }

  onColorPickerClose(event: string) {
    this.colorPickerClose.emit(event)
  }

  onColorPickerToggle(isOpen: boolean) {
    if (isOpen) {
      const currentColor = this.getPreviewColor(this.dropdownControl.value, this.colorPickerControl.value)
      this.colorPickerControl.setValue(currentColor, { emitEvent: false })
      this.colorPickerValue = currentColor
      this.dropdownControl.setValue('custom_color')
      this.cdr.markForCheck()
    }
  }

  onColorPickerValueChange(event: string) {
    this.colorPickerControl.setValue(event)
    this.cdr.markForCheck()
  }
}
